import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, CheckCircle2, FileCheck2, Link2, Rocket, ShieldCheck, Sparkles, Workflow } from 'lucide-react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { Section, fadeInUp } from '../components/Section';
import { trackLeadEvent } from '../lib/tracking';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROUTES = [
  { id: 'contrato-authlink', icon: FileCheck2, title: 'Contrato + Auth Link', copy: 'La ruta más directa para formalizar, autorizar y activar una empresa.' },
  { id: 'partner', icon: Rocket, title: 'Onboarding partner', copy: 'Alta comercial, acceso al panel y estructura inicial de partner.' },
  { id: 'clinica', icon: Workflow, title: 'Activación clínica', copy: 'Permisos por rol, fichas, procesos y continuidad operativa.' },
  { id: 'conexiones', icon: Link2, title: 'Conexiones embebidas', copy: 'Gmail, CRM, documentos y herramientas bajo consentimiento seguro.' },
];

const INCLUDED = [
  'Definición de la ruta de activación más útil según tu caso',
  'Solicitud orientada a contrato, consentimiento y onboarding sin pasos ambiguos',
  'Prioridad para entender qué herramientas debemos conectar primero',
  'Base clara para arrancar venta, panel, accesos y operación con continuidad',
];

export default function ActivationFlowPage() {
  const [formData, setFormData] = useState({
    nombre: '',
    email: '',
    empresa: '',
    country: '',
    telefono: '',
    route: 'contrato-authlink',
    urgency: 'esta-semana',
    mensaje: '',
    source: 'activation_flow_page',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [directActivation, setDirectActivation] = useState(null);
  const [submitError, setSubmitError] = useState('');

  const selectedRoute = ROUTES.find((item) => item.id === formData.route) || ROUTES[0];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError('');
    try {
      if (formData.route === 'contrato-authlink') {
        const response = await fetch(`${API}/public/demo-activation`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            full_name: formData.nombre,
            email: formData.email,
            company_name: formData.empresa,
            phone: formData.telefono,
            country: formData.country,
            message: formData.mensaje,
            origin_url: window.location.origin,
          }),
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.detail || 'No se pudo enviar el contrato');
        trackLeadEvent({ source: 'activation_flow_demo9' });
        setDirectActivation(data);
        setSubmitted(true);
        return;
      }

      const enrichedMessage = [
        `Ruta solicitada: ${selectedRoute.title}`,
        `Urgencia: ${formData.urgency}`,
        formData.telefono ? `Teléfono: ${formData.telefono}` : null,
        formData.mensaje || 'Sin detalle adicional.',
      ].filter(Boolean).join(' | ');

      const payload = {
        nombre: formData.nombre,
        email: formData.email,
        empresa: formData.empresa,
        country: formData.country,
        mensaje: enrichedMessage,
        source: formData.source,
      };

      const response = await fetch(`${API}/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (!data.success) throw new Error(data.detail || 'No se pudo enviar la solicitud');
      trackLeadEvent({ source: 'activation_flow_page' });
      setSubmitted(true);
    } catch (error) {
      setSubmitError(error.message || 'No se pudo completar la solicitud');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <SEO
        title="Activación directa | Neuralia369"
        description="Ruta directa para pedir contrato, Auth Link, onboarding y activación comercial en Neuralia369."
        path="/activar"
      />

      <div className="min-h-screen bg-background pt-24 pb-golden-2xl" data-testid="activation-page">
        <Section>
          <div className="max-w-7xl mx-auto px-golden-md lg:px-golden-lg grid gap-golden-xl xl:grid-cols-[0.92fr_1.08fr] items-start">
            <motion.div variants={fadeInUp}>
              <p className="editorial-kicker mb-golden-md" data-testid="activation-kicker">Ruta directa de activación</p>
              <h1 data-testid="activation-title">Pide <span className="gradient-text">contrato, Auth Link y onboarding</span> sin rodeos</h1>
              <p className="mt-golden-md text-lg text-text-body max-w-3xl" data-testid="activation-intro">
                Esta es la ruta más fuerte para convertir una intención en activación real. La usamos para ordenar rápido qué hay que formalizar, qué hay que conectar y cómo debe arrancar el cliente dentro de Neuralia369.
              </p>

              <div className="mt-golden-lg grid gap-4 md:grid-cols-2" data-testid="activation-routes-grid">
                {ROUTES.map(({ id, icon: Icon, title, copy }) => (
                  <button
                    key={id}
                    type="button"
                    onClick={() => setFormData((prev) => ({ ...prev, route: id }))}
                    className={`glass-card tracing-panel p-5 text-left transition-colors duration-300 ${formData.route === id ? 'border-primary/50 bg-primary/10' : ''}`}
                    data-testid={`activation-route-${id}`}
                  >
                    <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center text-primary">
                      <Icon className="w-6 h-6" />
                    </div>
                    <p className="mt-4 text-lg font-semibold text-text-heading">{title}</p>
                    <p className="mt-2 text-sm text-text-body">{copy}</p>
                  </button>
                ))}
              </div>

              <div className="mt-golden-lg glass-card tracing-panel p-golden-xl" data-testid="activation-included-card">
                <div className="flex items-center gap-3 text-text-heading">
                  <Sparkles className="w-5 h-5 text-primary" />
                  <h2 className="text-2xl font-semibold">Qué recibes al pedir esta ruta</h2>
                </div>
                <div className="mt-5 space-y-3">
                  {INCLUDED.map((item, index) => (
                    <div key={item} className="rounded-[22px] border border-border-subtle bg-background/55 p-4 flex items-start gap-3" data-testid={`activation-included-item-${index}`}>
                      <CheckCircle2 className="w-4 h-4 text-primary mt-1 shrink-0" />
                      <p className="text-sm text-text-body">{item}</p>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>

            <motion.div variants={fadeInUp} className="glass-card tracing-panel p-golden-xl" data-testid="activation-form-shell">
              {submitted ? (
                <div className="text-center p-golden-lg" data-testid="activation-success-state">
                  <div className="w-20 h-20 mx-auto rounded-full bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center">
                    <CheckCircle2 className="w-10 h-10 text-primary" />
                  </div>
                  <h2 className="mt-golden-md text-2xl text-text-heading font-heading font-semibold">{directActivation ? 'Contrato enviado' : 'Ruta solicitada'}</h2>
                  <p className="mt-3 text-text-body">{directActivation ? 'Te acabamos de enviar el contrato real por email. Fírmalo y luego continúa al pago desde la ruta post-firma.' : 'Perfecto. Ya tenemos tu solicitud orientada a contrato/Auth Link/onboarding. El siguiente paso será responderte con la vía más directa para activarlo.'}</p>
                  <div className="mt-golden-lg flex flex-wrap justify-center gap-3">
                    {directActivation ? (
                      <a href={directActivation.followup_url} className="btn-primary" data-testid="activation-success-followup-link">
                        Ver página post-firma
                        <ArrowRight className="w-4 h-4" />
                      </a>
                    ) : (
                      <Link to="/agenda" className="btn-primary" data-testid="activation-success-agenda-link">
                        Agendar llamada estratégica
                        <ArrowRight className="w-4 h-4" />
                      </Link>
                    )}
                    <Link to="/partners" className="btn-secondary" data-testid="activation-success-partners-link">
                      Ver programa partners
                    </Link>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4" data-testid="activation-form">
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Paso comercial más directo</p>
                    <h2 className="mt-2 text-2xl text-text-heading font-heading font-semibold">Solicitar activación</h2>
                    <p className="mt-2 text-sm text-text-body">Selecciona la ruta más parecida a tu necesidad. Si no está perfecta, nosotros la afinamos al responderte.</p>
                  </div>

                  <div className="rounded-[22px] border border-primary/15 bg-primary/5 p-4" data-testid="activation-selected-route-card">
                    <p className="text-xs uppercase tracking-[0.18em] text-primary">Ruta activa</p>
                    <p className="mt-2 text-lg font-semibold text-text-heading">{selectedRoute.title}</p>
                    <p className="mt-2 text-sm text-text-body">{selectedRoute.copy}</p>
                  </div>

                  {formData.route === 'contrato-authlink' && (
                    <div className="rounded-[22px] border border-secondary/20 bg-secondary/10 p-4 text-sm text-text-body" data-testid="activation-direct-offer-card">
                      Esta ruta ya activa el circuito completo: <strong>oferta → contrato → firma → pago demo 9 USD → onboarding</strong>. No cambia tus precios principales; sirve para validar y acelerar la activación comercial.
                    </div>
                  )}

                  <div className="grid md:grid-cols-2 gap-4">
                    <input type="text" className="input-kinesthetic" placeholder="Nombre" value={formData.nombre} onChange={(e) => setFormData({ ...formData, nombre: e.target.value })} required data-testid="activation-input-name" />
                    <input type="email" className="input-kinesthetic" placeholder="Email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} required data-testid="activation-input-email" />
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <input type="text" className="input-kinesthetic" placeholder="Empresa" value={formData.empresa} onChange={(e) => setFormData({ ...formData, empresa: e.target.value })} data-testid="activation-input-company" />
                    <input type="text" className="input-kinesthetic" placeholder="País" value={formData.country} onChange={(e) => setFormData({ ...formData, country: e.target.value })} data-testid="activation-input-country" />
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <input type="text" className="input-kinesthetic" placeholder="Teléfono / WhatsApp" value={formData.telefono} onChange={(e) => setFormData({ ...formData, telefono: e.target.value })} data-testid="activation-input-phone" />
                    <select className="input-kinesthetic" value={formData.urgency} onChange={(e) => setFormData({ ...formData, urgency: e.target.value })} data-testid="activation-input-urgency">
                      <option value="hoy">Lo necesito hoy</option>
                      <option value="esta-semana">Esta semana</option>
                      <option value="este-mes">Este mes</option>
                    </select>
                  </div>

                  <textarea className="input-kinesthetic min-h-[160px] resize-none" placeholder="Cuéntanos brevemente qué quieres activar primero" value={formData.mensaje} onChange={(e) => setFormData({ ...formData, mensaje: e.target.value })} required data-testid="activation-input-message" />

                  <div className="rounded-[20px] border border-border-subtle bg-card/35 px-4 py-4 flex items-start gap-3 text-sm text-text-body" data-testid="activation-security-card">
                    <ShieldCheck className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
                    <span>Usamos esta solicitud para orientarte a la ruta más directa: contrato, consentimiento, onboarding o conexión segura, sin pedir accesos sensibles por canales improvisados.</span>
                  </div>

                  {submitError && <div className="rounded-[18px] border border-red-500/25 bg-red-500/10 p-4 text-sm text-red-200" data-testid="activation-error-state">{submitError}</div>}

                  <button type="submit" className="btn-primary w-full justify-center" disabled={isSubmitting} data-testid="activation-submit-button">
                    {isSubmitting ? 'Enviando...' : <>{formData.route === 'contrato-authlink' ? 'Recibir contrato y pago demo 9 USD' : 'Solicitar contrato y activación'} <ArrowRight className="w-4 h-4" /></>}
                  </button>

                  <div className="flex flex-wrap gap-3" data-testid="activation-secondary-links">
                    <Link to="/agenda" className="btn-secondary" data-testid="activation-agenda-link">Prefiero agendar primero</Link>
                    <Link to="/contacto" className="btn-secondary" data-testid="activation-contact-link">Ir a contacto general</Link>
                  </div>
                </form>
              )}
            </motion.div>
          </div>
        </Section>
      </div>
    </>
  );
}